import dayjs, { Dayjs } from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(isoWeek);
dayjs.extend(customParseFormat);

export const HOURLY_RATE = 7000;

/* ================= TYPES ================= */

export interface BookUser {
  _id?: string;
  name?: string;
  fullName?: string;
  username?: string;
  userrealname?: string;
  role?: string;
}

export interface BookItem {
  _id: string;
  year?: string | number;
  day?: string; // "04-27"
  bookedTime?: string; // "13:00"
  packageName?: string;
  createdBy?: string | BookUser | null;
}

export interface DailyBookingRow extends BookItem {
  fullDate: string;
  durationMinutes: number;
  startDateTime: string;
  endDateTime: string;
  createdByName: string;
}

export interface UserSummary {
  key: string;
  user: string;
  bookings: number;
  workedHours: number;
  hourlyRate: number;
  salary: number;
  bookingList: DailyBookingRow[];
}

export interface DailySummary {
  key: string;
  date: string;
  totalBookings: number;
  totalWorkedHours: number;
  totalSalary: number;
  users: {
    user: string;
    bookings: number;
    workedHours: number;
    salary: number;
  }[];
  bookingList: DailyBookingRow[];
}

export interface EmployeeReportResult {
  startDate: Dayjs;
  endDate: Dayjs;
  userSummary: UserSummary[];
  dailySummary: DailySummary[];
  totalBookings: number;
  totalHours: number;
  totalSalary: number;
}

export type SalaryPeriodEndDay = 10 | 20 | 30;

/* ================= HELPERS ================= */

const normalizeText = (value: any): string => {
  return String(value || "")
    .trim()
    .toLowerCase();
};

const getCreatedByName = (createdBy: BookItem["createdBy"]): string => {
  if (!createdBy) return "Unknown";

  if (typeof createdBy === "string") {
    return createdBy || "Unknown";
  }

  return (
    createdBy.fullName ||
    createdBy.name ||
    createdBy.userrealname ||
    createdBy.username ||
    createdBy._id ||
    "Unknown"
  );
};

const getDurationMinutesFromPackage = (packageName?: string): number => {
  const lower = normalizeText(packageName);

  if (lower.includes("30")) return 30;
  if (lower.includes("60")) return 60;
  if (lower.includes("хосолсон")) return 60;
  if (lower.includes("хослосон")) return 60;

  // Хэрвээ packageName дээр 30/60 байхгүй бол booking-г алдахгүй,
  // гэхдээ цаг бодохын тулд default 60 минут гэж үзэж байна.
  return 60;
};

const getFullDate = (item: BookItem): Dayjs => {
  const year = String(item.year || "").trim();
  const day = String(item.day || "").trim();

  if (!year || !day) {
    return dayjs("");
  }

  // day = "04-27" гэж ирдэг тул ISO болгож parse хийнэ
  return dayjs(`${year}-${day}`, "YYYY-MM-DD", true);
};

const getStartDateTime = (item: BookItem, fullDate: string): Dayjs => {
  const bookedTime = String(item.bookedTime || "00:00").trim();

  // bookedTime "13" гэж ирвэл "13:00" болгоно
  const safeTime = bookedTime.includes(":") ? bookedTime : `${bookedTime}:00`;

  return dayjs(`${fullDate} ${safeTime}`, "YYYY-MM-DD HH:mm", true);
};

/* ================= OVERLAP FIX ================= */

type TimeRange = { start: number; end: number };

const mergeRanges = (ranges: TimeRange[]) => {
  const validRanges = ranges.filter(
    (r) => Number.isFinite(r.start) && Number.isFinite(r.end) && r.end > r.start
  );

  if (!validRanges.length) return [];

  const sorted = [...validRanges].sort((a, b) => a.start - b.start);
  const merged: TimeRange[] = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const last = merged[merged.length - 1];
    const cur = sorted[i];

    if (cur.start <= last.end) {
      last.end = Math.max(last.end, cur.end);
    } else {
      merged.push(cur);
    }
  }

  return merged;
};

const calculateHours = (rows: DailyBookingRow[]) => {
  const ranges = rows.map((r) => ({
    start: dayjs(r.startDateTime, "YYYY-MM-DD HH:mm", true).valueOf(),
    end: dayjs(r.endDateTime, "YYYY-MM-DD HH:mm", true).valueOf(),
  }));

  const merged = mergeRanges(ranges);

  const totalMinutes = merged.reduce(
    (sum, r) => sum + (r.end - r.start) / 60000,
    0
  );

  return Number((totalMinutes / 60).toFixed(2));
};

/* ================= MAIN ================= */

const buildEmployeeReportForRange = (
  books: BookItem[],
  startDate: Dayjs,
  endDate: Dayjs,
  hourlyRate: number = HOURLY_RATE
): EmployeeReportResult => {
  const rows: DailyBookingRow[] = books
    .map((book) => {
      const date = getFullDate(book);

      if (!date.isValid()) return null;

      const fullDate = date.format("YYYY-MM-DD");
      const durationMinutes = getDurationMinutesFromPackage(book.packageName);
      const start = getStartDateTime(book, fullDate);

      if (!start.isValid()) return null;

      const end = start.add(durationMinutes, "minute");
      const createdByName = getCreatedByName(book.createdBy);

      return {
        ...book,
        fullDate,
        durationMinutes,
        startDateTime: start.format("YYYY-MM-DD HH:mm"),
        endDateTime: end.format("YYYY-MM-DD HH:mm"),
        createdByName,
      } as DailyBookingRow;
    })
    .filter((row): row is DailyBookingRow => Boolean(row))
    .filter((row) => {
      const date = dayjs(row.fullDate, "YYYY-MM-DD", true);
    
      return (
        !date.isBefore(startDate, "day") &&
        !date.isAfter(endDate, "day")
      );
    });

  /* ================= USER SUMMARY ================= */

  const userMap: Record<string, UserSummary> = {};

  rows.forEach((row) => {
    const userKey = row.createdByName || "Unknown";

    if (!userMap[userKey]) {
      userMap[userKey] = {
        key: userKey,
        user: userKey,
        bookings: 0,
        workedHours: 0,
        hourlyRate,
        salary: 0,
        bookingList: [],
      };
    }

    userMap[userKey].bookings += 1;
    userMap[userKey].bookingList.push(row);
  });

  Object.values(userMap).forEach((user) => {
    const hours = calculateHours(user.bookingList);
    user.workedHours = hours;
    user.salary = Math.round(hours * hourlyRate);
  });

  /* ================= DAILY SUMMARY ================= */

  const dailyMap: Record<string, DailySummary> = {};

  rows.forEach((row) => {
    if (!dailyMap[row.fullDate]) {
      dailyMap[row.fullDate] = {
        key: row.fullDate,
        date: row.fullDate,
        totalBookings: 0,
        totalWorkedHours: 0,
        totalSalary: 0,
        users: [],
        bookingList: [],
      };
    }

    dailyMap[row.fullDate].totalBookings += 1;
    dailyMap[row.fullDate].bookingList.push(row);
  });

  Object.values(dailyMap).forEach((day) => {
    const userNames = Array.from(
      new Set(day.bookingList.map((b) => b.createdByName || "Unknown"))
    );

    day.users = userNames.map((userName) => {
      const userRows = day.bookingList.filter(
        (b) => b.createdByName === userName
      );

      const hours = calculateHours(userRows);

      return {
        user: userName,
        bookings: userRows.length,
        workedHours: hours,
        salary: Math.round(hours * hourlyRate),
      };
    });

    day.totalWorkedHours = Number(
      day.users.reduce((sum, user) => sum + user.workedHours, 0).toFixed(2)
    );

    day.totalSalary = day.users.reduce((sum, user) => sum + user.salary, 0);
  });

  const userSummary = Object.values(userMap).sort((a, b) =>
    a.user.localeCompare(b.user)
  );

  const dailySummary = Object.values(dailyMap).sort((a, b) =>
    a.date.localeCompare(b.date)
  );

  const totalBookings = rows.length;
  const totalHours = Number(
    userSummary.reduce((sum, user) => sum + user.workedHours, 0).toFixed(2)
  );
  const totalSalary = userSummary.reduce((sum, user) => sum + user.salary, 0);

  return {
    startDate,
    endDate,
    userSummary,
    dailySummary,
    totalBookings,
    totalHours,
    totalSalary,
  };
};

export const buildPeriodEmployeeReport = (
  books: BookItem[],
  users: string[],
  selectedDate: Dayjs,
  periodEndDay: SalaryPeriodEndDay,
  hourlyRate: number = HOURLY_RATE
): EmployeeReportResult => {
  const monthStart = selectedDate.startOf("month");
  const startDate =
    periodEndDay === 10
      ? monthStart
      : periodEndDay === 20
      ? monthStart.date(11)
      : monthStart.date(21);
  const endDate =
    periodEndDay === 10
      ? monthStart.date(10).endOf("day")
      : periodEndDay === 20
      ? monthStart.date(20).endOf("day")
      : selectedDate.endOf("month");

  return buildEmployeeReportForRange(books, startDate, endDate, hourlyRate);
};

export const buildWeeklyEmployeeReport = (
  books: BookItem[],
  users: string[],
  selectedDate: Dayjs,
  hourlyRate: number = HOURLY_RATE
): EmployeeReportResult => {
  return buildEmployeeReportForRange(
    books,
    selectedDate.startOf("isoWeek"),
    selectedDate.endOf("isoWeek"),
    hourlyRate
  );
};
